import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Check } from "lucide-react";
import { Reveal } from "../Reveal";
import { PRODUCTS } from "../../lib/site";

const ProductCard = ({ product, index }) => {
  const isComingSoon = product.comingSoon;
  const isLink = !!product.href;

  const Inner = (
    <>
      {/* Image / background */}
      {product.image && (
        <div className="absolute inset-0 z-0">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover opacity-60 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700 ease-out"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/20" />
        </div>
      )}

      <div className="relative z-10 flex flex-col justify-between h-full p-8 md:p-10">
        <div className="flex items-start justify-between">
          <span className="overline">
            {String(index + 1).padStart(2, "0")} / {product.tagline}
          </span>
          {product.badge ? (
            <span className="flex items-center gap-2 text-xs uppercase tracking-widest text-steel border border-steel/40 px-3 py-1 whitespace-nowrap shrink-0">
              {isComingSoon ? <Clock size={14} /> : <Check size={14} />}
              {product.badge}
            </span>
          ) : isLink ? (
            <ArrowUpRight
              size={28}
              className="text-textmuted group-hover:text-steel group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300"
            />
          ) : null}
        </div>

        <div>
          {product.logo && (
            <img
              src={product.logo}
              alt={`${product.name} logo`}
              className="h-16 md:h-20 w-auto object-contain mb-6 drop-shadow-lg"
            />
          )}
          <h3 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-textmain mb-3">
            {product.name}
          </h3>
          <p className="text-textmuted text-sm md:text-base leading-relaxed max-w-md whitespace-pre-line">
            {product.description}
          </p>
          {product.features && (
            <ul className="mt-6 space-y-2" data-testid={`product-features-${product.id}`}>
              {product.features.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-textmain">
                  <span className="w-1.5 h-1.5 bg-steel rotate-45 shrink-0" />
                  <span className="uppercase tracking-wide font-medium">{f}</span>
                </li>
              ))}
            </ul>
          )}
          {isLink && (
            <span className="inline-flex items-center gap-2 mt-6 text-sm font-semibold uppercase tracking-[0.15em] text-steel">
              Visit Site <ArrowUpRight size={16} />
            </span>
          )}
        </div>
      </div>
    </>
  );

  const baseClass = `group relative overflow-hidden bg-panel border transition-colors duration-500 ${product.span} ${
    isComingSoon
      ? "border-dashed border-white/15 hover:border-steel/40 min-h-[280px] md:min-h-[300px]"
      : "border-white/5 hover:border-steel/50 min-h-[420px] md:min-h-[520px]"
  }`;

  return (
    <Reveal delay={index * 0.1} className={product.span}>
      {isLink ? (
        <a
          href={product.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`product-card-${product.id}`}
          className={`${baseClass} block`}
        >
          {Inner}
        </a>
      ) : (
        <div data-testid={`product-card-${product.id}`} className={baseClass}>
          {Inner}
        </div>
      )}
    </Reveal>
  );
};

export const ProductGateway = () => {
  return (
    <section id="products" data-testid="product-gateway" className="relative bg-ink py-24 md:py-32 lg:py-40">
      <div className="container-x">
        <Reveal className="max-w-3xl mb-16">
          <p className="overline mb-5">The Product Gateway</p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-textmain leading-[0.95]">
            One Standard. Four Solutions.<br />Zero Compromise.
          </h2>
          <p className="mt-6 text-textmuted text-base md:text-lg max-w-xl">
            Every product exists for one reason — to fix a specific tactical failure
            inherent in "industry grade" gear.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {PRODUCTS.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
